<template>
    <div>

        <div class="mx-auto w-1/2 text-center">
            <h1 class="first-letter:uppercase text-heading-1 font-semibold mr-auto mb-16">
                {{ $t('import.step_3.title')}}
                <span v-if="job_column">{{ $t('import.step_3.title_for', { jobColumn: job_column})}}</span>
            </h1>
        </div>

        <div v-if="job_id" class="text-center">
            <p class="text-primary-80 mb-32">
                {{ $t('import.step_3.description1')}} <b>{{ jobName }}</b> {{ $t('import.step_3.description2')}}
            </p>

            <div class="grid grid-cols-1 gap-32 sm:grid-cols-3 lg:grid-cols-5 mx-auto">

                <div v-for="(stage, index) in stageOptions"
                     :class="stage.value === phase_id ? 'bg-secondary-5' : 'hover:border-primary-20 hover:bg-primary-5'"
                     class="relative flex items-center justify-center rounded-sm border border-primary-5 bg-white px-32 py-48 shadow-sm"
                >
                    <button class="cursor-pointer focus:outline-none"
                            role="button"
                            @click="setPhaseId(stage.value)"
                    >
                        <span aria-hidden="true" class="absolute inset-0"></span>

                        <span v-if="stage.value === phase_id" class="block text-secondary">
                            <svg-icon class="inline h-72 w-72" type="check-circle"/>
                        </span>
                        <progress-icon v-else
                                       :progress="( (index + 1)/ stageOptions.length) * 100"
                                       :show-icon="false"
                                       class="inline"
                                       size="72"></progress-icon>

                        <span class="block max-w-prose25 mt-42">
                            {{ stage.label }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
        <template v-else>

            <div class="mx-auto w-3/5 text-center mt-32">
                <p class="text-primary-80 mb-32" v-html="$t('import.step_3.new_job', { jobColumn: job_column})"></p>
            </div>

            <div class="flex items-center mx-auto justify-center w-2/5 mb-48">
                <p class="text-primary-80 mr-8">{{ $t('import.step_3.phase_title') }}</p>
                <select-dropdown
                    :auto-translate="false"
                    :options="fieldOptions"
                    :show-check-icon="true"
                    :value="phase_column"
                    :placeholder="$t('import.general.choose_field')"
                    searchable
                    @change="setPhase"
                />
            </div>


            <template v-if="phasesFound">
                <div class="text-center">
                    <h2 class="font-semibold text-heading-1 mb-16">{{ $t('import.step_3.define_phase') }}</h2>
                    <p class="text-primary-80 mb-32">{{ $t('import.step_3.define_phase_description') }}</p>
                </div>

                <div class="grid grid-cols-3 gap-32">
                    <select-dropdown v-model="phases.new"
                                     :auto-translate="false"
                                     :options="phasesOptions"
                                     multiple
                                     :placeholder="$t('import.general.choose_phase')"
                                     searchable>
                        <span class="flex items-center space-x-8">
                            <span>{{ $t('import.step_3.phase_new') }}:</span>
                            <progress-icon :progress="20"></progress-icon>
                        </span>
                    </select-dropdown>

                    <select-dropdown v-model="phases.onboarding"
                                     :auto-translate="false"
                                     :options="phasesOptions"
                                     multiple
                                     :placeholder="$t('import.general.choose_phase')"
                                     searchable>
                        <span class="flex items-center space-x-8">
                            <span>{{ $t('import.step_3.phase_hired') }}:</span>
                            <progress-icon :progress="100"></progress-icon>
                        </span>
                    </select-dropdown>

                    <select-dropdown v-model="phases.rejected"
                                     :auto-translate="false"
                                     :options="phasesOptions"
                                     multiple
                                     :placeholder="$t('import.general.choose_phase')"
                                     searchable>
                        <span class="flex items-center space-x-8">
                            <span>{{ $t('import.step_3.phase_rejected') }}:</span>
                            <progress-icon :progress="10" :rejected="true"></progress-icon>
                        </span>
                    </select-dropdown>
                </div>
            </template>
        </template>

        <div class="mt-48 flex justify-center space-x-20">
            <simple-button class="uppercase w-200"
                           look="secondary"
                           @click="$emit('previous')">
                {{ $t('general.back') }}
            </simple-button>

            <simple-button v-if="phase_id || (phasesFound && phasesFound.length)"
                           @click="savePhases"
                           display="secondary"
                           look="primary"
                           class="inline-flex font-semibold uppercase w-200">
                {{ $t('jobs.next_step') }}
            </simple-button>
        </div>
    </div>
</template>

<script>
import SelectDropdown from '../../forms/SelectDropdown';
import uniq from 'lodash/uniq';
import draggable from 'vuedraggable';
import ProgressIcon from '../ProgressIcon';
import Tooltip from '../../navigation/Tooltip';
import SvgIcon from '../SvgIcon';
import RoundButton from '../RoundButton';
import SimpleButton from '../SimpleButton';
import * as stages from '../../../services/StageService';
import Stage from '../../../models/Stage';
import {normalizeJson} from '../../../../shared/helpers';
import Job from '../../../models/Job';
import {useRepo} from 'pinia-orm';

export default {
    components: {
        SimpleButton,
        RoundButton,
        SvgIcon,
        Tooltip,
        ProgressIcon,
        SelectDropdown,
        draggable,
    },
    props: {
        file: File,
        data: Array,
        fields: Array,
        job_id: [Number, String],
        job_column: String,
        phase_id: [Number, String],
        phase_column: String,
        fieldOptions: Array,
    },
    data() {
        return {
            // Simple editor
            phases: {
                new: [],
                onboarding: [],
                rejected: [],
            },
        };
    },
    computed: {
        phasesFound() {
            if (!this.phase_column) {
                return null;
            }

            return uniq(this.data.map(app => app[this.phase_column]));
        },
        phasesOptions() {
            if (!this.phases) {
                return [];
            }

            return this.phasesFound.map(p => ({
                value: p,
                label: p,
            }));
        },
        stageOptions() {
            return useRepo(Stage).where('job_id', parseInt(this.job_id)).orderBy('order').get().map(stage => ({
                value: stage.id,
                label: stage.name,
            }));
        },
        jobName() {
            if (this.job_id) {
                return this.translated(useRepo(Job).find(this.job_id).title, undefined, true);
            }
        },
    },
    methods: {
        setPhase(phase_col) {
            this.$emit('setPhase', {phase_column: phase_col, phase_id: null});
        },
        setPhaseId(id) {
            this.$emit('setPhase', {phase_column: null, phase_id: id});
        },

        savePhases() {
            this.$emit('setPhases', this.phases);
            this.$emit('next');
        },
    },
    mounted() {
        if (this.job_id) {
            stages.index(this.job_id).then(({data}) => {
                useRepo(Stage).save(normalizeJson(data));
            });
        }
    },
};
</script>
